import { useState } from 'react'
import { Modal, Button, FormField, FormLabel, FormInput, FormTextarea, FormHint } from '.'
import { cronApi } from '@/api/cron'
import type { CronMonitor, CronMonitorCreate } from '@/types'

// Schedule presets with human-readable labels and interval in minutes
const SCHEDULE_PRESETS = [
  { value: 'every_5_minutes', label: 'Every 5 minutes', minutes: 5 },
  { value: 'every_15_minutes', label: 'Every 15 minutes', minutes: 15 },
  { value: 'every_30_minutes', label: 'Every 30 minutes', minutes: 30 },
  { value: 'every_hour', label: 'Every hour', minutes: 60 },
  { value: 'every_6_hours', label: 'Every 6 hours', minutes: 360 },
  { value: 'every_12_hours', label: 'Every 12 hours', minutes: 720 },
  { value: 'every_day', label: 'Every day', minutes: 1440 },
  { value: 'every_week', label: 'Every week', minutes: 10080 },
]

interface CreateCronMonitorModalProps {
  appId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (monitor: CronMonitor) => void
}

export function CreateCronMonitorModal({
  appId,
  open,
  onOpenChange,
  onCreated,
}: CreateCronMonitorModalProps) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [schedule, setSchedule] = useState('every_hour')
  const [gracePeriodMinutes, setGracePeriodMinutes] = useState(5)
  const [description, setDescription] = useState('')
  const [alertOnFailure, setAlertOnFailure] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value)
    // Generate slug from name: lowercase, replace spaces with dashes, remove special chars
    const generatedSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
    setSlug(generatedSlug)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const preset = SCHEDULE_PRESETS.find(p => p.value === schedule)
    if (!preset) {
      setError('Invalid schedule selected')
      setLoading(false)
      return
    }

    const data: CronMonitorCreate = {
      name,
      slug,
      schedule: preset.label,
      expected_interval_minutes: preset.minutes,
      grace_period_minutes: gracePeriodMinutes,
      description: description || undefined,
      alert_on_failure: alertOnFailure,
    }

    try {
      const monitor = await cronApi.createMonitor(appId, data)
      onCreated(monitor)
      // Reset form
      setName('')
      setSlug('')
      setSchedule('every_hour')
      setGracePeriodMinutes(5)
      setDescription('')
      setAlertOnFailure(true)
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create monitor'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Modal isOpen={open} onClose={handleClose} title="Create Cron Monitor">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-500 -mt-2 mb-4">
          Monitor a scheduled job by sending heartbeats after each run.
        </p>

        <FormField>
          <FormLabel htmlFor="name">Name</FormLabel>
          <FormInput
            id="name"
            placeholder="e.g., Daily Backup, Hourly Sync"
            value={name}
            onChange={e => handleNameChange(e.target.value)}
            required
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="slug">Slug (for heartbeat URL)</FormLabel>
          <FormInput
            id="slug"
            placeholder="e.g., daily-backup"
            value={slug}
            onChange={e => setSlug(e.target.value)}
            pattern="[a-z0-9][-a-z0-9]*"
            required
          />
          <FormHint>Lowercase letters, numbers, and dashes only</FormHint>
        </FormField>

        <FormField>
          <FormLabel htmlFor="schedule">Schedule</FormLabel>
          <select
            id="schedule"
            value={schedule}
            onChange={e => setSchedule(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {SCHEDULE_PRESETS.map(preset => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField>
          <FormLabel htmlFor="gracePeriod">Grace Period (minutes)</FormLabel>
          <FormInput
            id="gracePeriod"
            type="number"
            min={1}
            max={1440}
            value={gracePeriodMinutes}
            onChange={e => setGracePeriodMinutes(parseInt(e.target.value) || 5)}
          />
          <FormHint>How long to wait after the expected time before alerting</FormHint>
        </FormField>

        <FormField>
          <FormLabel htmlFor="description">Description (optional)</FormLabel>
          <FormTextarea
            id="description"
            placeholder="What does this job do?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
          />
        </FormField>

        <div className="flex items-center justify-between py-2">
          <label htmlFor="alertOnFailure" className="text-sm font-medium text-slate-700">
            Alert on missed heartbeat
          </label>
          <button
            type="button"
            role="switch"
            aria-checked={alertOnFailure}
            onClick={() => setAlertOnFailure(!alertOnFailure)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              alertOnFailure ? 'bg-blue-600' : 'bg-slate-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                alertOnFailure ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {error && <div className="text-sm text-red-500 bg-red-500/10 p-2 rounded">{error}</div>}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading || !name || !slug}>
            {loading ? 'Creating...' : 'Create Monitor'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
